import { AiEntityType, createMentionEntityTag } from '@diagon-alley/common';
import { PaperPlaneTiltIcon, UsersThreeIcon } from '@phosphor-icons/react';
import type { Editor } from '@tiptap/react';
import { type EntityTypeMeta } from './types';

type ProseMirrorNode = Parameters<
    Parameters<Editor['state']['doc']['forEach']>[0]
>[0];

/**
 * Get RichTextEditor styles based on streaming and hover state
 * @param isStreaming - Whether the AI is currently streaming
 * @param isSubmitStripHovered - Whether the submit strip is being hovered
 * @returns RichTextEditor styles object
 */
export const getRichTextEditorStyles = (
    isStreaming: boolean,
    isSubmitStripHovered: boolean = false,
) => ({
    root: {
        border: isSubmitStripHovered
            ? '1px solid rgb(var(--color-gray-400))'
            : '1px solid rgb(var(--color-gray-250))',
        borderRadius: '8px',
        backgroundColor: 'rgb(var(--color-white)/0.06)',
        opacity: isStreaming ? 0.7 : 1,
        transition: 'opacity 0.2s ease, border-color 0.2s ease',
        '&:hover': {
            border: '1px solid rgb(var(--color-gray-400))',
        },
        '&:focus-within': {
            border: '1px solid rgb(var(--color-gray-400))',
        },
        // Remove outline from all children, especially contenteditable
        '& *': {
            '&:focus': {
                outline: 'none !important',
            },
            '&:focus-visible': {
                outline: 'none !important',
            },
        },
        '& [contenteditable]': {
            outline: 'none !important',
            '&:focus': {
                outline: 'none !important',
            },
            '&:focus-visible': {
                outline: 'none !important',
            },
        },
    },
    content: {
        minHeight: 96, // Minimum height instead of fixed height
        maxHeight: 200, // Maximum height before scrolling
        overflowY: 'hidden' as const, // Hide overflow on content level
        backgroundColor: 'transparent',
        // Create physical space for submit buttons
        paddingBottom: 50,
        paddingLeft: 12,
        paddingRight: 12,
        paddingTop: 12,
        fontSize: 14,
        outline: 'none !important',
        boxSizing: 'border-box' as const,

        // Additional placeholder targeting at content level
        '& .is-empty[data-placeholder], & .is-editor-empty[data-placeholder]': {
            color: 'rgb(var(--color-gray-600))',
            fontSize: 12,
            fontWeight: 400,
        },
        // Target the TipTap editor more specifically
        '& .tiptap.ProseMirror': {
            paddingLeft: 12,
            paddingRight: 12,
            paddingTop: 12,
            paddingBottom: 12,
            fontSize: 14,
            color: 'rgb(var(--color-gray-800))',
            fontWeight: 500,
            minHeight: '1.375rem !important' as any, // Total height - button space - padding
            maxHeight: '7.5rem !important' as any, // Total height - button space - padding
            overflowY: 'auto !important' as any, // Force scrolling within the editor
            cursor: isStreaming ? 'not-allowed' : 'text',
            boxSizing: 'border-box' as const,
            outline: 'none !important',
            '&': {
                outline: 'none !important',
            },
            '&:focus': {
                outline: 'none !important',
            },
            '&:focus-visible': {
                outline: 'none !important',
            },
            '&[contenteditable="true"]': {
                outline: 'none !important',
                '&:focus': {
                    outline: 'none !important',
                },
                '&:focus-visible': {
                    outline: 'none !important',
                },
            },
            // Additional overrides for stubborn TipTap styles
            height: 'auto !important' as any,
            // Force scroll when content exceeds maxHeight
            '&[style*="height"]': {
                height: 'auto !important' as any,
                maxHeight: '7.5rem !important' as any,
            },
        },
        // Fallback for any .tiptap element
        '& > .tiptap': {
            paddingLeft: 12,
            paddingRight: 12,
            paddingTop: 12,
            paddingBottom: 12,
            fontSize: 14,
            color: 'rgb(var(--color-gray-800))',
            fontWeight: 500,
            minHeight: '1.375rem)',
            maxHeight: '7.5rem',
            overflowY: 'auto' as const,
            cursor: isStreaming ? 'not-allowed' : 'text',
            boxSizing: 'border-box' as const,
            // Target the placeholder using actual DOM structure
            '& .is-empty.is-editor-empty[data-placeholder]': {
                color: 'rgb(var(--color-gray-600))',
                fontSize: 12,
                fontWeight: 400,
            },
            '&.tiptap.ProseMirror .is-empty[data-placeholder]': {
                color: 'rgb(var(--color-gray-600))',
                fontSize: 12,
                fontWeight: 400,
            },
            '&.ProseMirror-focused': {
                outline: 'none !important',
            },
            '&:focus': {
                outline: 'none !important',
            },
            '&:focus-visible': {
                outline: 'none !important',
            },
        },
    },
});

/**
 * Entity type metadata for display
 */
export const BASE_ENTITY_TYPE_META: EntityTypeMeta = {
    [AiEntityType.WAREHOUSE]: {
        label: 'Warehouses',
        icon: UsersThreeIcon,
    },
    [AiEntityType.CHANNEL]: {
        label: 'Channels',
        icon: PaperPlaneTiltIcon,
    },
};

/**
 * Converts mentions in the editor content to markdown tags
 * @param editor - The TipTap editor instance
 * @returns The text content with mentions converted to markdown tags
 */
export const convertMentionsToMarkdown = (editor: Editor | null): string => {
    if (!editor) return '';

    const { state } = editor;
    const { doc } = state;
    let result = '';

    /**
     * Recursively processes the document nodes to build text with markdown mentions
     * @param node - The current document node
     * @returns The processed text for this node
     */
    const processNode = (node: ProseMirrorNode): string => {
        let text = '';

        if (node.type.name === 'mention' && node.attrs?.id) {
            const mentionAttrs = node.attrs;

            // Send plain label instead of a custom tag so backend gets the human-friendly name
            return mentionAttrs.label || mentionAttrs.id;
        }

        // Process text nodes
        if (node.isText) {
            return node.text || '';
        }

        // Process other nodes recursively
        if (node.content && node.content.size > 0) {
            node.forEach((child: ProseMirrorNode) => {
                text += processNode(child);
            });
        }

        return text;
    };

    doc.forEach((node: ProseMirrorNode) => {
        result += processNode(node);
    });

    return result;
};
